import { CommandInterface, NetworkType, BackendOptions, DosConfig,
    InitFsEntry, InitFileEntry, PersistedSockdrives, FrameBand } from "../emulators";
import { CommandInterfaceEventsImpl } from "../impl/ci-impl";
import { Drive, sockdrive } from "./sockdrive";
import { VirtualIpxServer, IpxRouteOutcome,
    isControlFrame, controlType, controlSlot,
    buildSlotRequest, buildSlotAssign,
    MP_CTRL_SLOT_REQUEST, MP_CTRL_SLOT_ASSIGN } from "./virtual-ipx-server";

const maxDataChunkSize = 4 * 1024 * 1024;

export type ClientMessage =
    "wc-install" |
    "wc-run" |
    "wc-pack-fs-to-bundle" |
    "wc-add-key" |
    "wc-mouse-move" |
    "wc-mouse-button" |
    "wc-mouse-sync" |
    "wc-exit" |
    "wc-sync-sleep" |
    "wc-pause" |
    "wc-resume" |
    "wc-mute" |
    "wc-unmute" |
    "wc-connect" |
    "wc-disconnect" |
    "wc-backend-event" |
    "wc-asyncify-stats" |
    "wc-fs-tree" |
    "wc-fs-get-file" |
    "wc-send-data-chunk" |
    "wc-net-connected" |
    "wc-net-received" |
    "wc-net-set-mac" |
    "wc-sockdrive-opened" |
    "wc-sockdrive-new-range" |
    "wc-sockdrive-preload-done" |
    "wc-sockdrive-restore-sectors" |
    "wc-unload" |
    "wc-fs-delete-file";

export type ServerMessage =
    "ws-extract-progress" |
    "ws-ready" |
    "ws-server-ready" |
    "ws-frame-set-size" |
    "ws-update-lines" |
    "ws-log" |
    "ws-warn" |
    "ws-err" |
    "ws-stdout" |
    "ws-exit" |
    "ws-persist" |
    "ws-sound-init" |
    "ws-sound-push" |
    "ws-config" |
    "ws-sync-sleep" |
    "ws-connected" |
    "ws-disconnected" |
    "ws-asyncify-stats" |
    "ws-fs-tree" |
    "ws-send-data-chunk" |
    "ws-net-connect" |
    "ws-net-disconnect" |
    "ws-net-send" |
    "ws-sockdrive-open" |
    "ws-sockdrive-ready" |
    "ws-sockdrive-close" |
    "ws-sockdrive-load-range" |
    "ws-sockdrive-write-sector" |
    "ws-sockdrive-range-loaded" |
    "ws-unload" |
    "ws-fs-delete-file" |
    // 3dfx voodoo OpenGL backend lifecycle, posted from voodoo_opengl.cpp
    // via the EM_JS helpers in worker-protocol.cpp.  See
    // docs/3dfx/40-runtime-wiring.md §6.
    "ws-voodoo-active" |
    "ws-voodoo-inactive" |
    "ws-voodoo-resize" |
    // Phase 7-r6 — 1 Hz voodoo swap-rate sample.  Drives the FPS overlay
    // in src/window/dos/fps-overlay.tsx via redux dos.voodoo.fps.
    "ws-perf-fps";

export type MessageHandler = (name: ServerMessage, props: { [key: string]: any }) => void;

export interface TransportLayer {
    sessionId: string;
    sendMessageToServer(name: ClientMessage,
        props: { [key: string]: any },
        transfer?: (ArrayBuffer | OffscreenCanvas)[]): void;
    initMessageHandler(handler: MessageHandler): void;
    exit?: () => void;
}

export interface FrameLine {
    start: number;
    heapu8: Uint8Array;
}

export interface DataChunk {
    type: "ok" | "file" | "bundle";
    name: string;
    data: ArrayBuffer | null;
}

// js-dos 8.4.0 Turbo-panel CPU-metrics (backported). Parsed from the wire
// string emitted by the wasm getCPUMetrics() export; consumed by the Turbo
// React panel.
export interface CPUMetrics {
    cpuMax: number;
    emulatorSpeed: number;
    cpuUsed: number;
    frameSkip: number;
    fastForward: boolean;
    cpuSockdrive: boolean;
    cpuAuto: boolean;
    cpuSkip: boolean;
    sleepPattern: string;
    ratio: number[];
    newCmax: number[];
    ticksDone: number[];
    ticksScheduled: number[];
}

export interface AsyncifyStats {
    offscreenCanvas?: boolean,
    messageSent: number,
    messageReceived: number,
    messageFrame: number,
    messageSound: number,
    nonSkippableSleepCount: number,
    sleepCount: number,
    sleepTime: number,
    cycles: number,
    // js-dos 8.4.0 Turbo-panel CPU-metrics (null until the wasm emits a sample).
    cpuMetrics: CPUMetrics | null,
    netSent: number,
    netRecv: number,
    driveIo: {
        url: string,
        preload: number,
        total: number,
        read: number,
        write: number,
    }[];
    /** Drives still opening — not in `driveIo` yet. */
    driveOpening: number;
}

export interface FsNode {
    name: string,
    size: number | null,
    nodes: FsNode[] | null,
}

export class CommandInterfaceOverTransportLayer implements CommandInterface {
    private startedAt = Date.now();
    private exited = false;
    private frameWidth = 0;
    private frameHeight = 0;
    private rgb: Uint8Array | null = null;
    private rgba: Uint8Array | null = null;
    private freq = 0;
    private utf8Decoder = new TextDecoder();

    private init?: InitFsEntry[];
    private transport: TransportLayer;
    private ready: (err: Error | null) => void;

    private persistPromise?: Promise<Uint8Array | PersistedSockdrives | null>;
    private persistResolve?: (bundle: Uint8Array | PersistedSockdrives | null) => void;

    private exitPromise?: Promise<void>;
    private exitResolve?: () => void;

    private eventsImpl = new CommandInterfaceEventsImpl();

    private keyMatrix: { [keyCode: number]: boolean } = {};

    private configPromise: Promise<DosConfig>;
    private configResolve: (config: DosConfig) => void = () => {/**/};
    private panicMessages: string[] = [];

    private connectPromise: Promise<void> | null = null;
    private connectResolve: () => void = () => {/**/};
    private connectReject: () => void = () => {/**/};

    private disconnectPromise: Promise<void> | null = null;
    private disconnectResolve: () => void = () => {/**/};

    private asyncifyStatsPromise: Promise<AsyncifyStats> | null = null;
    private asyncifyStatsResolve: (stats: AsyncifyStats) => void = () => {/**/};

    private fsTreePromise: Promise<FsNode> | null = null;
    private fsTreeResolve: (fsRoot: FsNode) => void = () => {/**/};

    private fsGetFilePromise: { [name: string]: Promise<Uint8Array> } = {};
    private fsGetFileResolve: { [name: string]: (file: Uint8Array) => void } = {};
    private fsGetFileParts: { [name: string]: Uint8Array[] } = {};

    private fsDeleteFilePromise: Promise<boolean> | null = null;
    private fsDeleteFileResolve: (deleted: boolean) => void = () => {/**/};

    private dataChunkPromise: { [name: string]: Promise<void> } = {};
    private dataChunkResolve: { [name: string]: () => void } = {};
    private networkId = 0;
    // A network entry is one of:
    //   • WebSocket — the legacy IPX-over-WS path (still used by single-
    //     player sockdrive and admin-configured raw IPX backends).
    //   • host-self — this peer's wasm bound to the in-process
    //     VirtualIpxServer as slot 1.
    //   • peer-to-host — this peer is a guest, outbound bytes are piped
    //     to the host's WebRTC channel; its slot is assigned by the
    //     host (the SLOT_REQUEST/SLOT_ASSIGN handshake below), not
    //     hardcoded, so 3+ guests get distinct node ids.
    // The slot is the wasm's IPX node id (00:00:00:00:00:<slot>), pushed
    // into the wasm via `wc-net-set-mac` once the host assigns it.
    private network: { [id: number]:
        | WebSocket
        | { kind: "host-self";    clientId: string; slot: number }
        | { kind: "peer-to-host"; hostPeerId: number; slot: number }
    } = {};

    /**
     * In-process IPX server. Created lazily on the *host* side when
     * the wasm calls `ws-net-connect` with "self"; guests never
     * instantiate one (they pipe bytes to the host instead).
     */
    private virtualIpxServer: VirtualIpxServer | null = null;
    // Guest-side reverse map: webrtc peer id → networkId. The host
    // side never reads this (VirtualIpxServer owns the peer book).
    private webrtcByPeer: Map<number, number> = new Map();
    private webrtcPollHandle: ReturnType<typeof setInterval> | null = null;
    // Poll cadence for the webrtcnet recv pump (ms). Matches webrtcnet's
    // internal wait granularity; used by both the main-thread interval and
    // the background worker-timer below.
    private static readonly WEBRTC_POLL_MS = 4;
    // The pump body, extracted so it can be driven by EITHER the main-thread
    // interval (foreground) or a worker-timer (backgrounded tab). Non-null
    // once ensureWebrtcPoll has run — used as the "already installed" guard
    // since webrtcPollHandle goes null while the worker-timer is driving.
    private webrtcPumpFn: (() => void) | null = null;
    // Background keep-alive: a dedicated worker whose setInterval is NOT
    // clamped to ~1 Hz the way a hidden tab's main-thread timers are, so the
    // WebRTC/SCTP heartbeat keeps being serviced while the tab is hidden and
    // the datachannel is not torn down. Only active while document.hidden;
    // foreground always uses the tuned main-thread interval. See
    // installBackgroundPump().
    private bgTicker: Worker | null = null;
    private bgTickerUrl: string | null = null;
    private bgVisibilityHandler: (() => void) | null = null;
    // Event-driven receive fast-path (foreground). The patched webrtcnet glue
    // calls net.onData the instant a datachannel message lands in the wasm recv
    // queue; we drain it via this MessageChannel on the next macrotask instead
    // of waiting up to WEBRTC_POLL_MS for the poll — cutting the 0-4ms per-hop
    // latency + jitter the bridge otherwise adds. `recvDrainScheduled`
    // coalesces a burst of onData callbacks into a single drain. See
    // ensureWebrtcPoll().
    private recvChannel: MessageChannel | null = null;
    private recvDrainScheduled = false;
    // Guest-side pending slot handshakes, keyed by host peer id. While
    // an entry is present the guest's wasm is still asyncify-blocked in
    // em_net_connect — we defer wc-net-connected until the host answers
    // our SLOT_REQUEST. `ticks` counts 4ms poll wakeups for resend +
    // fallback timing.
    private guestPendingSlot: Map<number,
        { networkId: number; hostPeerId: number; ticks: number }> = new Map();
    // First N packets per direction get the full descriptor (dst/src
     // MAC + first 16 bytes of payload) so we can read game lobby
     // protocols from the console. After the budget, only every 50th
     // packet ticks the counter so the console stays quiet.
    private static readonly IPX_FULL_TRACE_BUDGET = 10;
    // IPX header offsets — packed Novell layout, 30 bytes.
    private static readonly IPX_HEADER_SIZE = 30;
    private static readonly IPX_DEST_NODE   = 10;
    private static readonly IPX_DEST_SOCKET = 16;
    private static readonly IPX_SRC_NODE    = 22;
    private static readonly IPX_SRC_SOCKET  = 28;
    // How many webrtcnet packets the recv pump processes per 4 ms
    // tick. Asymmetric on purpose:
    //   • HOST runs `VirtualIpxServer` *plus* its own wasm on the
    //     pinned main thread — fanout + sync wc-net-received per
    //     packet is significant work. Keep it low so DOM keyup /
    //     mousemove events get a guaranteed slot between batches;
    //     without that, fire-release events queue behind state-sync
    //     bursts and the player gets "stuck shooting".
    //   • GUEST only pumps bytes into its own wasm — no IPX server.
    //     A bursty input-lockstep game (BLOOD: ~10-30 packets in one
    //     scene change) gated at 8/tick would stall the wasm for
    //     12-16 ms waiting for the tail of the burst to arrive,
    //     producing the visible occasional hitch. A higher cap
    //     drains the burst in a single tick.
    // 12 × 250 Hz = 3000 pps (host), 32 × 250 Hz = 8000 pps (guest)
    // — both well above the ~70 pps any DOS-era IPX game generates.
    private static readonly IPX_DRAIN_HOST  = 12;
    private static readonly IPX_DRAIN_GUEST = 32;
    private ipxOutCount = 0;
    private ipxInCount = 0;
    // Wire-traffic byte counters folded into asyncifyStats.netSent /
    // netRecv so the sidebar stats panel reflects WebRTC IPX traffic.
    // The C++ counters in SDLnetTCP only see legacy TCP and stay at 0
    // in MP, so the panel was always blank for net stats.
    private ipxSentBytes = 0;
    private ipxRecvBytes = 0;
    // Cached once at construction. window.dosgamesIpxTrace lets devs
    // opt in to per-packet tracing via `?ipxtrace=1`; in production
    // the flag is undefined and tracing is fully short-circuited.
    private readonly traceEnabled: boolean = (() => {
        try {
            return typeof globalThis !== "undefined"
                && (globalThis as { dosgamesIpxTrace?: boolean }).dosgamesIpxTrace === true;
        } catch (_e) {
            return false;
        }
    })();

    /** Lower-cased colon-separated 6-byte MAC at `offset`. */
    private static formatMac(data: Uint8Array, offset: number): string {
        const hex = (b: number) => b.toString(16).padStart(2, "0");
        return hex(data[offset]) + ":" + hex(data[offset + 1]) + ":"
             + hex(data[offset + 2]) + ":" + hex(data[offset + 3]) + ":"
             + hex(data[offset + 4]) + ":" + hex(data[offset + 5]);
    }

    /** Concatenated hex of a byte slice. */
    private static formatHex(data: Uint8Array, from: number, to: number): string {
        const end = Math.min(to, data.byteLength);
        let out = "";
        for (let i = from; i < end; i++) out += data[i].toString(16).padStart(2, "0");
        return out;
    }

    /**
     * Trace one packet through the bridge. `dir` is from the bridge's
     * point of view: OUT means the wasm sent it (we are forwarding
     * onwards), IN means it arrived from the wire and we are about to
     * hand it to the wasm or route it through the server.
     */
    private tracePacket(dir: "IN" | "OUT", who: string, data: Uint8Array, outcome: string): void {
        // Always accumulate byte totals so the stats panel reflects MP
        // traffic. OUT = sent by this peer's wasm, IN = received from
        // the wire (or the local IPX server in host-self mode).
        if (dir === "OUT") this.ipxSentBytes += data.byteLength;
        else                this.ipxRecvBytes += data.byteLength;
        if (!this.traceEnabled) return;
        const C = CommandInterfaceOverTransportLayer;
        if (dir === "OUT") this.ipxOutCount++; else this.ipxInCount++;
        const counter = dir === "OUT" ? this.ipxOutCount : this.ipxInCount;
        if (counter <= C.IPX_FULL_TRACE_BUDGET) {
            if (data.byteLength >= C.IPX_HEADER_SIZE) {
                const dstMac    = C.formatMac(data, C.IPX_DEST_NODE);
                const srcMac    = C.formatMac(data, C.IPX_SRC_NODE);
                const dstSocket = ((data[C.IPX_DEST_SOCKET]   << 8) | data[C.IPX_DEST_SOCKET   + 1]).toString(16);
                const srcSocket = ((data[C.IPX_SRC_SOCKET]    << 8) | data[C.IPX_SRC_SOCKET    + 1]).toString(16);
                const payloadEnd  = Math.min(data.byteLength, C.IPX_HEADER_SIZE + 16);
                const payloadHex  = C.formatHex(data, C.IPX_HEADER_SIZE, payloadEnd);
                const payloadMore = data.byteLength > payloadEnd ? "…" : "";
                console.log("[ipx-pkt] " + dir + " #" + counter
                    + " " + who + " → " + outcome
                    + " len=" + data.byteLength
                    + " dst=" + dstMac + ":" + dstSocket
                    + " src=" + srcMac + ":" + srcSocket
                    + " payload=" + payloadHex + payloadMore);
            } else {
                console.log("[ipx-pkt] " + dir + " #" + counter
                    + " " + who + " → " + outcome
                    + " len=" + data.byteLength + " (sub-header)");
            }
        } else if (counter % 50 === 0) {
            console.log("[ipx-pkt] " + dir + " total=" + counter);
        }
    }

    private sockdrives: { [handle: number]: Drive } = {};
    /** Drives whose open is still fetching their layout and boot pack. The
     *  emulator is blocked in imgmount meanwhile, and nothing else says so. */
    private sockdrivesOpening = new Set<number>();

    public options: BackendOptions;

    constructor(init: InitFsEntry[],
        transport: TransportLayer,
        ready: (err: Error | null) => void,
        options: BackendOptions) {
        this.options = options;
        this.init = init;
        this.transport = transport;
        this.ready = ready;
        this.configPromise = new Promise<DosConfig>((resolve) => this.configResolve = resolve);
        this.transport.initMessageHandler(this.onServerMessage.bind(this));
    }

    /**
     * Start the webrtcnet receive pump. Idempotent — only one timer
     * runs per session. Each tick:
     *   1. Drives the webrtcnet wasm event loop via `net.wait(0)`
     *      (drains libwebsockets signaling traffic and libwebrtc
     *      state-machine progress into the recvfrom queue).
     *   2. Pulls up to N packets from the queue.
     *   3. On the *host* side, hands each packet to the in-process
     *      `VirtualIpxServer`. The server adopts the sender as a
     *      client on first sight (assigning the next sequential
     *      slot / MAC), then routes per dest MAC: REG → reply, FF…
     *      → fan-out, otherwise → unicast lookup.
     *   4. On the *guest* side, the host's reply lands here and is
     *      forwarded verbatim to the local wasm via wc-net-received.
     *
     * Without `net.wait()` SCTP heartbeats and libwebrtc keepalives
     * starve — symptom is a host that loses communication several
     * seconds into gameplay. Polling at 4ms matches webrtcnet's
     * internal wait granularity; recvBinary returns null fast when
     * the queue is empty so idle CPU stays near zero.
     */
    private ensureWebrtcPoll(net: NonNullable<BackendOptions["net"]>): void {
        if (this.webrtcPumpFn !== null) return;
        const C = CommandInterfaceOverTransportLayer;

        // Bounded receive drain, extracted so it can be driven by BOTH the 4 ms
        // poll (foreground interval / background worker-timer) AND the event-
        // driven MessageChannel fast-path installed below. Returns true when it
        // stopped because it hit the cap (more may be waiting) so the event-
        // driven caller can re-schedule another batch after a macrotask yield.
        //
        // Bounded on purpose. Anything beyond the cap waits for the next wakeup
        // so DOM input events (keyup, mouse) get a chance to fire between
        // batches — without the cap, a 30+ packet state-sync burst (BLOOD scene
        // change) drains in one go, fires that many sync wasm reentries inline,
        // and the player's fire-release stays queued behind the burst → "stuck
        // shooting". Sync delivery (no microtask) keeps the wasm's wsBuffers in
        // wire order. Cap is asymmetric: guest's main thread is much lighter
        // than host's (no IPX server), so a higher cap there absorbs lockstep
        // bursts without leaving the wasm starved for the tail packets of a tic.
        const drainRecv = (): boolean => {
            const cap = this.virtualIpxServer ? C.IPX_DRAIN_HOST : C.IPX_DRAIN_GUEST;
            let drained = 0;
            for (; drained < cap; drained++) {
                const msg = net.recvBinary();
                if (!msg) break;

                if (this.virtualIpxServer) {
                    // HOST side. Route through the in-process IPX server.
                    // First packet (or SLOT_REQUEST) from a peer auto-
                    // registers it; the sink closes over `peerId` so
                    // server routing back to this peer goes out via WebRTC.
                    const peerId = msg.peerId;
                    const clientId = "peer:" + peerId;
                    const isNew = !this.virtualIpxServer.has(clientId);
                    const mac = this.virtualIpxServer.register(clientId, (pkt) => {
                        try { net.sendBinary(pkt, peerId); } catch (_e) { /* ignore */ }
                    });
                    if (isNew) {
                        console.log("[mp] peer joined: " + peerId + " slot=" + mac[5]
                            + " (peers=" + this.virtualIpxServer.size() + ")");
                    }
                    // Slot handshake: answer the guest's SLOT_REQUEST with
                    // the slot allocSlot just gave it. register() is
                    // idempotent so resends return the same slot. Control
                    // frames are never routed as IPX.
                    if (isControlFrame(msg.data)) {
                        if (controlType(msg.data) === MP_CTRL_SLOT_REQUEST) {
                            try { net.sendBinary(buildSlotAssign(mac[5]), peerId); } catch (_e) { /* guest retries */ }
                        }
                        continue;
                    }
                    const outcome = this.virtualIpxServer.deliver(clientId, msg.data);
                    this.tracePacket("IN", clientId, msg.data, outcome);
                    continue;
                }

                // GUEST side. Find the networkId this peer is bound to
                // (only the host peer is in the map) and dispatch the
                // bytes synchronously. wasm reentry via direct.ts
                // module.messageHandler is in-process and cheap; the
                // earlier microtask deferral preserved order but ran
                // before any DOM task, so 50 packets fired 50 wasm
                // reentries with no input dispatch in between.
                const networkId = this.webrtcByPeer.get(msg.peerId);
                if (networkId === undefined) {
                    console.warn("[mp] dropped packet from unknown peer " + msg.peerId);
                    continue;
                }
                // Bridge control frame: the host's answer to our
                // SLOT_REQUEST. Apply the slot + release the deferred
                // wc-net-connected; never forward to the wasm.
                if (isControlFrame(msg.data)) {
                    if (controlType(msg.data) === MP_CTRL_SLOT_ASSIGN) {
                        this.resolveGuestSlot(msg.peerId, controlSlot(msg.data));
                    }
                    continue;
                }
                // Until the slot handshake completes the wasm network is
                // not connected yet (wc-net-connected deferred), so drop
                // any stray game packets rather than hit "network is not
                // registered" or deliver them out of order.
                if (this.guestPendingSlot.has(msg.peerId)) {
                    continue;
                }
                this.tracePacket("IN", "host", msg.data, "wasm");
                const payload = msg.data.buffer;
                this.sendClientMessage("wc-net-received", {
                    networkId,
                    data: payload,
                }, [payload]);
            }
            return drained === cap;
        };

        // Periodic bookkeeping the pump runs regardless of packet arrival:
        // drive the wasm event loop (SCTP / libwebsockets heartbeat), resend
        // the guest slot handshake, and reconcile the peer set. drainRecv runs
        // here too as a safety net (idempotent — the event-driven fast-path may
        // already have emptied the queue; whichever runs first wins, and
        // single-threaded JS means neither can reorder the shared FIFO).
        const pump = () => {
            try { net.wait(0); } catch (_e) { /* lib gone, pump dies on next tick */ }
            drainRecv();

            // Guest slot handshake: resend SLOT_REQUEST until the host
            // answers with SLOT_ASSIGN, then fall back to the legacy
            // slot 2 after ~5s so a lone reachable host never hangs.
            // Empty on the host — it never populates guestPendingSlot.
            if (this.guestPendingSlot.size > 0) {
                for (const pending of Array.from(this.guestPendingSlot.values())) {
                    pending.ticks++;
                    if (pending.ticks % 25 === 0) {
                        try { net.sendBinary(buildSlotRequest(), pending.hostPeerId); } catch (_e) { /* retry next tick */ }
                    }
                    if (pending.ticks > 1250) {
                        console.warn("[mp] slot handshake timed out for host "
                            + pending.hostPeerId + ", falling back to slot 2");
                        this.resolveGuestSlot(pending.hostPeerId, 2);
                    }
                }
            }

            // Reconcile the VirtualIpxServer with webrtcnet's live peer
            // set. When a guest closes their tab the webrtcnet wasm
            // drops the peer from `net.connected` (recvfrom returns
            // -1 with the peer id), but nothing else clears its sink
            // — broadcasts kept fanning into a dead datachannel and
            // the host's wasm sat in lockstep waiting for ACKs that
            // would never arrive. Walk the snapshot, unregister any
            // client whose peer is no longer connected, and log it so
            // the join/leave pair stays symmetric in the console.
            if (this.virtualIpxServer && this.virtualIpxServer.size() > 0) {
                for (const clientId of this.virtualIpxServer.ids()) {
                    if (!clientId.startsWith("peer:")) continue;
                    const peerId = Number(clientId.slice(5));
                    if (!Number.isFinite(peerId) || net.connected.has(peerId)) continue;
                    this.virtualIpxServer.unregister(clientId);
                    console.log("[mp] peer left: " + peerId
                        + " (peers=" + this.virtualIpxServer.size() + ")");
                }
            }
        };
        this.webrtcPumpFn = pump;

        // Event-driven receive fast-path. webrtcnet's patched glue calls
        // net.onData (→ Module.onChannelData) synchronously the instant a
        // datachannel message lands in the wasm recv queue, so we can drain it
        // now instead of waiting up to WEBRTC_POLL_MS (4 ms) for the next poll
        // tick — the only steady-state latency the bridge adds, and pure jitter
        // over a WAN link. We drain via a MessageChannel task, NOT inline:
        //   • a MessageChannel post is a real macrotask NOT subject to the
        //     setTimeout / nested-setInterval 4 ms clamp, so it fires in well
        //     under a ms — but still AFTER the current task, so DOM input keeps
        //     its slot and we never reenter recvBinary from inside onmessage.
        //   • `recvDrainScheduled` coalesces a burst of N onData callbacks into
        //     ONE drain; if that drain hits the cap it re-posts, so the
        //     per-batch yield the cap depends on survives (a macrotask boundary
        //     between batches, same intent as the old 4 ms spacing).
        // The interval + background worker-timer stay on as the heartbeat
        // driver and a safety-net drain (e.g. while the tab is hidden, where a
        // MessageChannel task may be paused). Both drains pull the same FIFO on
        // one thread, so they can never double-deliver or reorder a packet.
        const chan = typeof MessageChannel !== "undefined" ? new MessageChannel() : null;
        if (chan) {
            this.recvChannel = chan;
            const scheduleDrain = () => {
                if (this.recvDrainScheduled) return;
                this.recvDrainScheduled = true;
                chan.port2.postMessage(0);
            };
            chan.port1.onmessage = () => {
                this.recvDrainScheduled = false;
                // Torn down between the post and its delivery — do nothing.
                if (this.webrtcPumpFn === null) return;
                if (drainRecv()) scheduleDrain();
            };
            net.onData = scheduleDrain;
        }

        // Foreground driver: the tuned main-thread interval, untouched.
        this.webrtcPollHandle = setInterval(pump, C.WEBRTC_POLL_MS);
        // Background driver: swap to a worker-timer while the tab is hidden so
        // the pump (and the SCTP heartbeat it services) keeps running.
        this.installBackgroundPump();
    }

    /**
     * Keep the webrtcnet pump alive while the tab is hidden. A background tab
     * clamps main-thread `setInterval` to ~1 Hz (and to ~1/min under intensive
     * throttling), which starves the SCTP heartbeat serviced by `net.wait()`
     * and tears the datachannel down — exactly the "connection drops when I
     * switch tabs" bug. A dedicated worker's timer is not subject to that
     * visibility throttling, so on `visibilitychange` we hand the pump to a
     * worker-timer while hidden and back to the main-thread interval when
     * visible. The two are mutually exclusive, so the recv-drain cap is never
     * double-pumped, and the foreground cadence is exactly as before.
     *
     * No-op outside a browser (CI/Node has no `document`/`Worker`) — there the
     * plain interval installed above is all that runs, which is fine.
     */
    private installBackgroundPump(): void {
        if (typeof document === "undefined" || typeof Worker === "undefined") return;
        if (this.bgVisibilityHandler !== null) return;
        const POLL_MS = CommandInterfaceOverTransportLayer.WEBRTC_POLL_MS;
        const handler = () => {
            if (this.webrtcPumpFn === null) return;
            if (document.hidden) {
                if (this.webrtcPollHandle !== null) {
                    clearInterval(this.webrtcPollHandle);
                    this.webrtcPollHandle = null;
                }
                if (this.bgTicker === null) {
                    // A tiny self-contained worker: setInterval that just posts
                    // back on each tick. Built from a Blob so no separate asset
                    // is needed in the bundle. The URL is kept alive (revoked on
                    // teardown) to avoid an early-revoke race during worker load.
                    const src =
                        "let h=null;onmessage=function(e){var d=e.data;" +
                        "if(d&&d.stop){if(h!==null){clearInterval(h);h=null;}return;}" +
                        "if(h!==null){clearInterval(h);}" +
                        "h=setInterval(function(){postMessage(0);},(d&&d.ms)||4);};";
                    const url = URL.createObjectURL(new Blob([src], { type: "text/javascript" }));
                    const worker = new Worker(url);
                    worker.onmessage = () => { if (this.webrtcPumpFn !== null) this.webrtcPumpFn(); };
                    worker.postMessage({ ms: POLL_MS });
                    this.bgTicker = worker;
                    this.bgTickerUrl = url;
                }
            } else {
                this.stopBgTicker();
                if (this.webrtcPollHandle === null && this.webrtcPumpFn !== null) {
                    this.webrtcPollHandle = setInterval(this.webrtcPumpFn, POLL_MS);
                }
            }
        };
        this.bgVisibilityHandler = handler;
        document.addEventListener("visibilitychange", handler);
        // Honour an already-hidden tab at connect time (opened in background).
        if (document.hidden) handler();
    }

    private stopBgTicker(): void {
        if (this.bgTicker !== null) {
            this.bgTicker.terminate();
            this.bgTicker = null;
        }
        if (this.bgTickerUrl !== null) {
            URL.revokeObjectURL(this.bgTickerUrl);
            this.bgTickerUrl = null;
        }
    }

    /** Tear down every pump driver + its visibility wiring. Called from exit(). */
    private teardownWebrtcPoll(): void {
        if (this.webrtcPollHandle !== null) {
            clearInterval(this.webrtcPollHandle);
            this.webrtcPollHandle = null;
        }
        this.stopBgTicker();
        if (this.bgVisibilityHandler !== null && typeof document !== "undefined") {
            document.removeEventListener("visibilitychange", this.bgVisibilityHandler);
        }
        this.bgVisibilityHandler = null;
        // Detach the event-driven fast-path: drop the glue's callback and close
        // the MessageChannel so no queued drain can run after teardown.
        const net = this.options.net;
        if (net) net.onData = null;
        if (this.recvChannel !== null) {
            this.recvChannel.port1.onmessage = null;
            this.recvChannel.port1.close();
            this.recvChannel.port2.close();
            this.recvChannel = null;
        }
        this.recvDrainScheduled = false;
        this.webrtcPumpFn = null;
    }

    /**
     * Apply the slot the host assigned to this guest and release the
     * deferred connect. Safe to call once per host peer: the pending
     * entry is removed so a late duplicate SLOT_ASSIGN (or the fallback)
     * is ignored. Order matters — set the MAC while the wasm is still
     * suspended in em_net_connect, THEN connect, so the _ConnectToServer
     * poll fast-paths on the correct node id straight away.
     */
    private resolveGuestSlot(hostPeerId: number, slot: number): void {
        const pending = this.guestPendingSlot.get(hostPeerId);
        if (!pending) return;
        this.guestPendingSlot.delete(hostPeerId);
        const entry = this.network[pending.networkId];
        if (entry && !(entry instanceof WebSocket) && entry.kind === "peer-to-host") {
            entry.slot = slot;
        }
        console.log("[mp] host assigned slot=" + slot + " (host peer " + hostPeerId + ")");
        this.sendClientMessage("wc-net-set-mac", { networkId: pending.networkId, slot });
        this.sendClientMessage("wc-net-connected", { networkId: pending.networkId });
    }

    private sendClientMessage(name: ClientMessage, props?: { [key: string]: any }, transfer?: [ArrayBuffer]) {
        props = props || {};
        props.sessionId = props.sessionId || this.transport.sessionId;
        this.transport.sendMessageToServer(name, props, transfer);
    }


    private onServerMessage(name: ServerMessage, props: { [key: string]: any }) {
        if (name === undefined || name.length < 3 ||
            name[0] !== "w" || name[1] !== "s" || name[2] !== "-") {
            return;
        }

        if (props === undefined || props.sessionId !== this.transport.sessionId) {
            return;
        }

        switch (name) {
            case "ws-ready": {
                const sendBundles = async () => {
                    if (!this.init || this.init.length === 0) {
                        return;
                    }

                    const encoder = new TextEncoder();
                    const sendData = async (type: "file" | "bundle", name: string, contents: Uint8Array) => {
                        await this.sendDataChunk({
                            type,
                            name,
                            data: contents.buffer,
                        });

                        await this.sendDataChunk({
                            type,
                            name,
                            data: null,
                        });
                    };

                    let bundleIndex = 0;
                    for (const next of this.init) {
                        if (ArrayBuffer.isView(next)) {
                            await sendData("bundle", bundleIndex + "", next);
                            bundleIndex++;
                        } else if (typeof next === "string") {
                            await sendData("file", ".jsdos/dosbox.conf", encoder.encode(next));
                        } else {
                            const fileEntry = next as InitFileEntry;
                            const dosConfig = next as DosConfig;

                            if (dosConfig.jsdosConf?.version !== undefined) {
                                await sendData("file", ".jsdos/dosbox.conf",
                                    encoder.encode(dosConfig.dosboxConf));
                                await sendData("file", ".jsdos/jsdos.json",
                                    encoder.encode(JSON.stringify(dosConfig.jsdosConf, null, 2)));
                            } else if (fileEntry.path !== undefined) {
                                await sendData("file", fileEntry.path, fileEntry.contents);
                            } else {
                                console.error("Unknown init part", next);
                            }
                        }
                    }
                };

                sendBundles()
                    .then(() => {
                        this.sendClientMessage("wc-run", { token: this.options.token });
                    })
                    .catch((e) => {
                        this.onErr("panic", "Can't send bundles to backend: " + e.message);
                        console.error(e);
                    })
                    .finally(() => {
                        delete this.init;
                    });
            } break;
            case "ws-server-ready": {
                if (this.panicMessages.length > 0) {
                    if (this.transport.exit !== undefined) {
                        this.transport.exit();
                    }
                    this.ready(new Error(JSON.stringify(this.panicMessages)));
                } else {
                    this.ready(null);
                }
                delete (this as any).ready;
            } break;
            case "ws-frame-set-size": {
                this.onFrameSize(props.width, props.height);
            } break;
            case "ws-voodoo-active": {
                this.eventsImpl.fireVoodooActive(props.width, props.height);
            } break;
            case "ws-voodoo-inactive": {
                this.eventsImpl.fireVoodooInactive();
            } break;
            case "ws-voodoo-resize": {
                this.eventsImpl.fireVoodooResize(props.width, props.height);
            } break;
            case "ws-perf-fps": {
                this.eventsImpl.firePerformanceFps(props.fps);
            } break;
            case "ws-update-lines": {
                this.onFrameLines(props.lines, props.rgba);
            } break;
            case "ws-exit": {
                this.onExit();
            } break;
            case "ws-log": {
                // eslint-disable-next-line
                this.onLog(props.tag, props.message);
            } break;
            case "ws-warn": {
                // eslint-disable-next-line
                this.onWarn(props.tag, props.message);
            } break;
            case "ws-err": {
                // eslint-disable-next-line
                this.onErr(props.tag, props.message);
            } break;
            case "ws-stdout": {
                this.onStdout(props.message);
            } break;
            case "ws-persist": {
                this.onPersist(props.bundle ?? props.sockdrives ?? null);
            } break;
            case "ws-sound-init": {
                this.onSoundInit(props.freq);
            } break;
            case "ws-sound-push": {
                this.onSoundPush(props.samples);
            } break;
            case "ws-config": {
                this.onConfig({
                    dosboxConf: this.utf8Decoder.decode(props.dosboxConf),
                    jsdosConf: JSON.parse(props.jsdosConf),
                });
            } break;
            case "ws-sync-sleep": {
                this.sendClientMessage("wc-sync-sleep", props);
            } break;
            case "ws-connected": {
                this.connectResolve();
                this.connectPromise = null;
                this.connectResolve = () => {/**/};
                this.connectReject = () => {/**/};
                this.eventsImpl.fireNetworkConnected(props.networkType, props.address);
            } break;
            case "ws-disconnected": {
                if (this.connectPromise !== null) {
                    this.connectReject();
                    this.connectPromise = null;
                    this.connectResolve = () => {/**/};
                    this.connectReject = () => {/**/};
                } else {
                    this.disconnectResolve();
                    this.disconnectPromise = null;
                    this.disconnectResolve = () => {/**/};
                }
                this.eventsImpl.fireNetworkDisconnected(props.networkType);
            } break;
            case "ws-extract-progress": {
                if (this.options.onExtractProgress) {
                    this.options.onExtractProgress(props.index, props.file, props.extracted, props.count);
                }
            } break;
            case "ws-asyncify-stats": {
                props.driveIo = [];
                for (const drive of Object.values(this.sockdrives)) {
                    props.driveIo.push({
                        url: drive.info.url,
                        preload: drive.info.preloadSizeInBytes,
                        total: drive.info.sizeInBytes,
                        read: drive.info.readInBytes,
                        write: drive.info.writeInBytes,
                    });
                }
                props.driveOpening = this.sockdrivesOpening.size;
                // Fold WebRTC IPX traffic into the wasm-side counters
                // so the sidebar Net SENT/Net RECV rows reflect MP
                // bytes — the C++ counters only see legacy TCP and
                // never tick on the IPX-over-WebRTC bridge.
                props.netSent = (props.netSent || 0) + this.ipxSentBytes;
                props.netRecv = (props.netRecv || 0) + this.ipxRecvBytes;
                // js-dos 8.4.0 Turbo-panel CPU-metrics: parse the wasm wire
                // string into a CPUMetrics object for the Turbo React panel.
                if (typeof props.cpuMetrics === "string" && props.cpuMetrics.length > 0) {
                    const partial = props.cpuMetrics.split(" ");
                    const cpu = partial[0].split("|");
                    const metrics: CPUMetrics = {
                        cpuMax: parseInt(cpu[0]),
                        emulatorSpeed: parseInt(cpu[1]) / 100,
                        cpuUsed: parseInt(cpu[2]) / 100,
                        frameSkip: parseInt(cpu[3]),
                        fastForward: cpu[4][0] === "1",
                        cpuSockdrive: cpu[4][1] === "1",
                        cpuAuto: cpu[4][2] === "1",
                        cpuSkip: cpu[4][3] === "1",
                        sleepPattern: "",
                        ratio: [],
                        newCmax: [],
                        ticksDone: [],
                        ticksScheduled: [],
                    };

                    for (let i = 1; i < partial.length; i++) {
                        if (partial[i][0] === "r") {
                            const parts = partial[i].split("|");
                            metrics.ratio.push(parseInt(parts[0].substring(1)) / 1024);
                            metrics.newCmax.push(parseInt(parts[1]));
                            metrics.ticksDone.push(parseInt(parts[2]));
                            metrics.ticksScheduled.push(parseInt(parts[3]));
                        } else {
                            metrics.sleepPattern += partial[i] + " ";
                        }
                    }
                    props.cpuMetrics = metrics;
                } else {
                    props.cpuMetrics = null;
                }
                this.asyncifyStatsResolve(props as AsyncifyStats);
                this.asyncifyStatsResolve = () => {/**/};
                this.asyncifyStatsPromise = null;
            } break;
            case "ws-fs-tree": {
                this.fsTreeResolve(props.fsTree as FsNode);
                this.fsTreeResolve = () => {/**/};
                this.fsTreePromise = null;
            } break;
            case "ws-fs-delete-file": {
                this.fsDeleteFileResolve(props.deleted);
                this.fsDeleteFileResolve = () => {/**/};
                this.fsDeleteFilePromise = null;
            } break;
            case "ws-send-data-chunk": {
                const chunk: DataChunk = props.chunk;
                const key = this.dataChunkKey(chunk);
                if (chunk.type === "ok") {
                    if (this.dataChunkPromise[key] !== undefined) {
                        this.dataChunkResolve[key]();
                        delete this.dataChunkPromise[key];
                        delete this.dataChunkResolve[key];
                    }
                } else if (chunk.type === "file") {
                    if (chunk.data === null) {
                        const file = this.mergeChunks(this.fsGetFileParts[chunk.name]);
                        this.fsGetFileResolve[chunk.name](file);
                        delete this.fsGetFilePromise[chunk.name];
                        delete this.fsGetFileResolve[chunk.name];
                    } else {
                        this.fsGetFileParts[chunk.name].push(new Uint8Array(chunk.data));
                    }
                } else {
                    console.log("Unknown chunk type:", chunk.type);
                }
            } break;
            case "ws-net-connect": {
                this.networkId += 1;
                const networkId = this.networkId;
                const net = this.options.net;
                // Webrtcnet routing: the literal "self" sentinel marks
                // host mode (spin up the in-process IPX server and bind
                // the local wasm as its first client); a bare numeric
                // address is a host peer id (guest → host pipe). The
                // ws://self / wss://self variants come from older
                // callers — accept both.
                if (net) {
                    if (props.address === "self"
                        || props.address === "wss://self"
                        || props.address === "ws://self") {
                        if (!this.virtualIpxServer) {
                            this.virtualIpxServer = new VirtualIpxServer();
                        }
                        const server = this.virtualIpxServer;
                        const clientId = "self";
                        // Sink invoked by VirtualIpxServer when the wasm
                        // should receive a packet. We must copy because
                        // the server may route the wasm's own outbound
                        // buffer (self-loop) and the wasm reuses that
                        // buffer on the next ws-net-send tick. Delivery
                        // is synchronous on purpose — direct.ts
                        // module.messageHandler is in-process, and the
                        // earlier microtask deferral just shifted the
                        // sync wasm reentries to right after this task
                        // (microtasks block DOM event dispatch the same
                        // way an inline sync call does). The IPX_DRAIN
                        // _PER_TICK cap is what actually gives input a
                        // chance to interleave.
                        const mac = server.register(clientId, (pkt) => {
                            const copy = new Uint8Array(pkt.byteLength);
                            copy.set(pkt);
                            this.sendClientMessage("wc-net-received", {
                                networkId,
                                data: copy.buffer,
                            }, [copy.buffer]);
                        });
                        const hostSlot = mac[5];
                        console.log("[mp] hosting as peer " + net.peerId + ", slot=" + hostSlot);
                        this.network[networkId] = { kind: "host-self", clientId, slot: hostSlot };
                        this.ensureWebrtcPoll(net);
                        this.sendClientMessage("wc-net-connected", { networkId });
                        this.sendClientMessage("wc-net-set-mac", { networkId, slot: hostSlot });
                        break;
                    }
                    const peerMatch = /^(?:wss?:\/\/)?(\d+)\/?$/.exec(props.address);
                    if (peerMatch) {
                        const hostPeerId = Number.parseInt(peerMatch[1], 10);
                        // The host's VirtualIpxServer assigns our slot
                        // (allocSlot) — we must NOT hardcode it, or 3+
                        // guests all collide on node 2 and the lobby never
                        // separates them. Defer wc-net-connected (the wasm
                        // asyncify-blocks in em_net_connect with no timeout)
                        // until the host answers SLOT_REQUEST with
                        // SLOT_ASSIGN; the poll resends + falls back.
                        console.log("[mp] joining via host " + hostPeerId + ", requesting slot…");
                        this.network[networkId] = { kind: "peer-to-host", hostPeerId, slot: 0 };
                        this.webrtcByPeer.set(hostPeerId, networkId);
                        this.guestPendingSlot.set(hostPeerId, { networkId, hostPeerId, ticks: 0 });
                        this.ensureWebrtcPoll(net);
                        // Kick off immediately; the datachannel may not be
                        // open yet (send lost) — the poll retries.
                        try { net.sendBinary(buildSlotRequest(), hostPeerId); } catch (_e) { /* poll retries */ }
                        break;
                    }
                }
                // Legacy IPX-over-WebSocket — unchanged. Reachable when
                // the address is a real ws/wss URL or when no webrtcnet
                // handle is attached (single-player or sockdrive-only).
                const socket = new WebSocket(props.address);
                socket.binaryType = "arraybuffer";
                socket.addEventListener("error", (e) => {
                    console.error("Can't connect to", props.address);
                    this.sendClientMessage("wc-net-connected", { networkId: -1 });
                });
                socket.addEventListener("open", () => {
                    this.network[networkId] = socket;
                    this.sendClientMessage("wc-net-connected", { networkId });
                });
                socket.addEventListener("message", (message) => {
                    this.sendClientMessage("wc-net-received", {
                        networkId,
                        data: message.data,
                    }, [message.data]);
                });
            } break;
            case "ws-net-send": {
                const entry = this.network[props.networkId];
                if (entry instanceof WebSocket) {
                    entry.send(props.data);
                    break;
                }
                if (!entry) break;
                const net = this.options.net;
                if (!net) break;

                const data = props.data instanceof ArrayBuffer
                    ? new Uint8Array(props.data)
                    : props.data;

                if (entry.kind === "host-self") {
                    // Feed the wasm's outbound packet straight to the
                    // in-process IPX server. The server inspects dst.node
                    // and either replies to a REG-REQUEST, fans out a
                    // broadcast, unicasts to the owner of dst, or drops
                    // when no client owns it yet (the wasm retransmits).
                    const outcome: IpxRouteOutcome =
                        this.virtualIpxServer!.deliver(entry.clientId, data);
                    this.tracePacket("OUT", entry.clientId, data, outcome);
                } else {
                    // Guest pipe: ship verbatim to the host. The host's
                    // VirtualIpxServer adopts us on first packet and
                    // routes everything from there.
                    this.tracePacket("OUT", "host", data, "wire");
                    try { net.sendBinary(data, entry.hostPeerId); } catch (_e) { /* ignore */ }
                }
            } break;
            case "ws-net-disconnect": {
                const entry = this.network[props.networkId];
                delete this.network[props.networkId];
                if (entry instanceof WebSocket) {
                    entry.close();
                } else if (entry) {
                    if (entry.kind === "host-self") {
                        this.virtualIpxServer?.unregister(entry.clientId);
                    } else if (entry.kind === "peer-to-host") {
                        this.webrtcByPeer.delete(entry.hostPeerId);
                        this.guestPendingSlot.delete(entry.hostPeerId);
                    }
                }
                // Leave the recv pump running — other entries may
                // still need it; it stops on session unload below.
            } break;
            case "ws-sockdrive-open": {
                const handle = props.handle;
                let url =
                    props.url
                        .replace("wss://sockdrive.js-dos.com:8001/dos.zone/",
                            "https://br.cdn.dos.zone/sockdrive-qcow2/dos.zone-")
                        .replace("wss://sockdrive.js-dos.com:8001/system/",
                            "https://br.cdn.dos.zone/sockdrive-qcow2/system-");
                if (url.endsWith("/")) {
                    url = url.slice(0, -1);
                }
                // A drive the page built itself (localDrives) — under its own
                // spelling of the name, which its saves are filed under.
                const local = this.options.localDrives?.find((d) =>
                    d.url.toLowerCase() === url.toLowerCase());
                if (local) {
                    url = local.url;
                }
                this.sockdrivesOpening.add(handle);
                sockdrive(url, (range, buffer) => {
                    this.sendClientMessage("wc-sockdrive-new-range", {
                        handle: handle,
                        range: range,
                        buffer,
                    });
                }, local).then((drive) => {
                    this.sockdrives[props.handle] = drive;
                    const emptyRanges = Array.from(drive.info.dropped_ranges);
                    this.sendClientMessage("wc-sockdrive-opened", {
                        handle: handle,
                        size: drive.info.size,
                        heads: drive.info.heads,
                        cylinders: drive.info.cylinders,
                        sectors: drive.info.sectors,
                        sectorSize: drive.info.sector_size,
                        aheadRange: drive.info.ahead_read,
                        sizeBytes: drive.info.size_bytes ?? 0,
                        emptyRangesCount: drive.info.dropped_ranges.length,
                        emptyRanges,
                    });
                }).catch((e: Error) => {
                    this.onErr("panic", "Can't open sockdrive(" + url + "): " + e.message);
                    console.error(e);

                    this.sendClientMessage("wc-sockdrive-opened", {
                        handle: handle,
                        size: 0,
                        heads: 0,
                        cylinders: 0,
                        sectors: 0,
                        sectorSize: 0,
                        aheadRange: 0,
                        sizeBytes: 0,
                        emptyRangesCount: 0,
                        emptyRanges: [],
                    });
                }).finally(() => this.sockdrivesOpening.delete(handle));
            } break;
            case "ws-sockdrive-ready": {
                const handle = props.handle;
                const drive = this.sockdrives[handle];
                // Replay previously persisted in-game writes into the wasm's
                // sector-level write cache BEFORE any game reads run: ranges
                // can now arrive via the wasm's own synchronous fetch, which
                // bypasses this side's range merge — the wasm must know the
                // written sectors itself (it checks the write cache first).
                const written = drive.writtenSectors();
                if (written.length > 0) {
                    const sectorSize = drive.info.sector_size;
                    const payload = new Uint8Array(4 + written.length * (4 + sectorSize));
                    const view = new DataView(payload.buffer);
                    view.setUint32(0, written.length, true);
                    let offset = 4;
                    for (const { sector, data } of written) {
                        view.setUint32(offset, sector, true);
                        payload.set(data.subarray(0, sectorSize), offset + 4);
                        offset += 4 + sectorSize;
                    }
                    this.sendClientMessage("wc-sockdrive-restore-sectors",
                        { handle, data: payload });
                }
                // Drain the LISTED preload queue into the wasm RangeCache and
                // only then release the blocked sockdrive_open — that covers
                // the boot-critical ranges. Everything else streams on demand
                // through the wasm's synchronous fetch. Notify even on
                // failure — a wedged open (infinite "Starting…") is worse
                // than falling back to on-demand loads.
                drive.ready()
                    .catch((e) => console.error("sockdrive preload failed", e))
                    .finally(() => {
                        this.sendClientMessage("wc-sockdrive-preload-done", { handle });
                    });
            } break;
            case "ws-sockdrive-load-range": {
                this.sockdrives[props.handle].readRangeAsync(props.range);
            } break;
            case "ws-sockdrive-write-sector": {
                this.sockdrives[props.handle].write(props.sector, props.data);
            } break;
            case "ws-sockdrive-range-loaded": {
                // The wasm fetched this range synchronously itself (on-demand
                // streaming) — persist it into the shared IDB store so the
                // next session's open finds it locally.
                const drive = this.sockdrives[props.handle];
                if (drive) {
                    drive.storeRange(props.range, props.data)
                        .catch(() => {/* quota/private mode — next session refetches */});
                }
            } break;
            case "ws-sockdrive-close": {
                delete this.sockdrives[props.handle];
            } break;
            case "ws-unload": {
                this.eventsImpl.fireUnload().finally(() => {
                    this.sendClientMessage("wc-unload");
                });
            } break;
            default: {
                // eslint-disable-next-line
                console.log("Unknown server message (ws):", name);
            } break;
        }
    }

    private onConfig(config: DosConfig) {
        this.configResolve(config);
    }

    private onFrameSize(width: number, height: number) {
        if (this.frameWidth === width && this.frameHeight === height) {
            return;
        }

        this.frameWidth = width;
        this.frameHeight = height;
        this.rgb = new Uint8Array(width * height * 3);
        this.eventsImpl.fireFrameSize(width, height);
    }

    private onFrameLines(lines: FrameLine[], rgbaPtr: number) {
        const rgb = this.rgb;
        if (!rgb) {
            return;
        }
        const rowBytes = this.frameWidth * 3;
        const bands: FrameBand[] = [];
        for (const line of (lines as FrameLine[])) {
            // Lines queued before a mode switch can fall outside the new,
            // smaller frame; a set() past the end throws a RangeError and
            // takes the frame with it. Drop them — the next frame redraws.
            const offset = line.start * rowBytes;
            if (offset + line.heapu8.length > rgb.length) {
                continue;
            }
            rgb.set(line.heapu8, offset);
            bands.push({ start: line.start, count: line.heapu8.length / rowBytes });
        }

        // The worker already knows which rows changed; handing them on lets
        // the renderer upload those rows instead of the whole frame.
        this.eventsImpl.fireFrame(this.rgb, this.rgba, bands);
    }

    private onSoundInit(freq: number) {
        this.freq = freq;
    }

    private onSoundPush(samples: Float32Array) {
        this.eventsImpl.fireSoundPush(samples);
    }

    private onLog(tag: string, message: string) {
        this.eventsImpl.fireMessage("log", "[" + tag + "]" + message);
    }

    private onWarn(tag: string, message: string) {
        this.eventsImpl.fireMessage("warn", "[" + tag + "]" + message);
    }

    private onErr(tag: string, message: string) {
        if (tag === "panic") {
            this.panicMessages.push(message);
            console.error("[" + tag + "]" + message);
        }
        this.eventsImpl.fireMessage("error", "[" + tag + "]" + message);
    }

    private onStdout(message: string) {
        this.eventsImpl.fireStdout(message);
    }

    public config() {
        return this.configPromise;
    }

    public width() {
        return this.frameWidth;
    }

    public height() {
        return this.frameHeight;
    }

    public soundFrequency() {
        return this.freq;
    }

    public screenshot(): Promise<ImageData> {
        if (this.rgb !== null || this.rgba !== null) {
            const rgba = new Uint8ClampedArray(this.frameWidth * this.frameHeight * 4);
            const frame = (this.rgb !== null ? this.rgb : this.rgba) as Uint8Array;

            let frameOffset = 0;
            let rgbaOffset = 0;

            while (rgbaOffset < rgba.length) {
                rgba[rgbaOffset++] = frame[frameOffset++];
                rgba[rgbaOffset++] = frame[frameOffset++];
                rgba[rgbaOffset++] = frame[frameOffset++];
                rgba[rgbaOffset++] = 255;

                if (frame.length === rgba.length) {
                    frameOffset++;
                }
            }

            return Promise.resolve(new ImageData(rgba, this.frameWidth, this.frameHeight));
        } else {
            return Promise.reject(new Error("No frame received"));
        }
    }

    public simulateKeyPress(...keyCodes: number[]) {
        const timeMs = Date.now() - this.startedAt;
        keyCodes.forEach((keyCode) => this.addKey(keyCode, true, timeMs));
        keyCodes.forEach((keyCode) => this.addKey(keyCode, false, timeMs + 16));
    }

    public sendKeyEvent(keyCode: number, pressed: boolean) {
        this.addKey(keyCode, pressed, Date.now() - this.startedAt);
    }

    // public for test
    public addKey(keyCode: number, pressed: boolean, timeMs: number) {
        const keyPressed = this.keyMatrix[keyCode] === true;
        if (keyPressed === pressed) {
            return;
        }
        this.keyMatrix[keyCode] = pressed;
        this.sendClientMessage("wc-add-key", { key: keyCode, pressed, timeMs });
    }

    public sendMouseMotion(x: number, y: number) {
        this.sendClientMessage("wc-mouse-move", { x, y, relative: false, timeMs: Date.now() - this.startedAt });
    }

    public sendMouseRelativeMotion(x: number, y: number) {
        this.sendClientMessage("wc-mouse-move", { x, y, relative: true, timeMs: Date.now() - this.startedAt });
    }

    public sendMouseButton(button: number, pressed: boolean) {
        this.sendClientMessage("wc-mouse-button", { button, pressed, timeMs: Date.now() - this.startedAt });
    }

    public sendMouseSync() {
        this.sendClientMessage("wc-mouse-sync", { timeMs: Date.now() - this.startedAt });
    }

    public sendBackendEvent(payload: any) {
        this.sendClientMessage("wc-backend-event", { json: JSON.stringify(payload) });
    }


    public async persist(optOnlyChanges?: boolean): Promise<Uint8Array | PersistedSockdrives | null> {
        const onlyChanges = optOnlyChanges ?? true;
        if (this.persistPromise !== undefined) {
            return this.persistPromise;
        }

        const sockdrives = await this.persistSockdrives();
        if (sockdrives !== null && onlyChanges) {
            return Promise.resolve(sockdrives);
        }

        const persistPromise = new Promise<Uint8Array | PersistedSockdrives | null>((resolve) => {
            this.persistResolve = resolve;
        });
        this.persistPromise = persistPromise;
        this.sendClientMessage("wc-pack-fs-to-bundle", {
            onlyChanges,
        });

        return persistPromise;
    }

    private onPersist(bundle: Uint8Array | PersistedSockdrives | null) {
        if (this.persistResolve) {
            this.persistResolve(bundle);
            delete this.persistPromise;
            delete this.persistResolve;
        }
    }

    public pause() {
        this.sendClientMessage("wc-pause");
    }

    public resume() {
        this.sendClientMessage("wc-resume");
    }

    public mute() {
        this.sendClientMessage("wc-mute");
    }

    public unmute() {
        this.sendClientMessage("wc-unmute");
    }

    public exit(): Promise<void> {
        if (this.exited) {
            return Promise.resolve();
        }
        if (this.exitPromise !== undefined) {
            return this.exitPromise;
        }
        this.exitPromise = new Promise<void>((resolve) => this.exitResolve = resolve);
        this.exitPromise.then(() => {
            this.events().fireExit();
        });

        this.resume();
        for (const next of Object.values(this.network)) {
            // Only WebSocket entries need an active close call — webrtcnet
            // bindings live in JS-side maps that are dropped below, and the
            // shared webrtcnet handle is owned by the parent js-dos and
            // torn down with `net.shutdown()` separately.
            if (next instanceof WebSocket) next.close();
        }
        this.network = {};
        this.webrtcByPeer.clear();
        this.guestPendingSlot.clear();
        this.virtualIpxServer = null;
        this.teardownWebrtcPoll();
        this.sendClientMessage("wc-exit");

        return this.exitPromise;
    }

    private onExit() {
        if (!this.exited) {
            this.exited = true;
            if (this.transport.exit !== undefined) {
                this.transport.exit();
            }
            if (this.exitResolve) {
                this.exitResolve();
                delete this.exitPromise;
                delete this.exitResolve;
            }
        }
    }

    public events() {
        return this.eventsImpl;
    }

    public networkConnect(networkType: NetworkType, address: string): Promise<void> {
        if (this.connectPromise !== null || this.disconnectPromise !== null) {
            return Promise.reject(new Error("Already prefoming connection or disconnection..."));
        }

        this.connectPromise = new Promise<void>((resolve, reject) => {
            // Webrtcnet sentinels (numeric peer id, the "self" listener
            // tag) are NOT URLs — preserving them lets the bridge
            // below detect and route through the P2P transport. Only
            // hostnames get the ws:// prefix dance the legacy IPX-over-
            // WebSocket path expects.
            const isWebrtcSentinel = /^\d+$/.test(address) || address === "self";
            if (!isWebrtcSentinel
                && !address.startsWith("wss://")
                && !address.startsWith("ws://")) {
                address = (window.location.protocol === "http:" ? "ws://" : "wss://") + address;
            }

            this.connectResolve = resolve;
            this.connectReject = reject;
            this.sendClientMessage("wc-connect", {
                networkType,
                address,
            });
        });
        return this.connectPromise;
    }

    public networkDisconnect(networkType: NetworkType): Promise<void> {
        if (this.connectPromise !== null || this.disconnectPromise !== null) {
            return Promise.reject(new Error("Already prefoming connection or disconnection..."));
        }

        this.disconnectPromise = new Promise<void>((resolve) => {
            this.disconnectResolve = resolve;

            this.sendClientMessage("wc-disconnect", {
                networkType,
            });
        });
        return this.disconnectPromise;
    }

    public asyncifyStats(): Promise<AsyncifyStats> {
        if (this.asyncifyStatsPromise !== null) {
            return this.asyncifyStatsPromise;
        }

        const promise = new Promise<AsyncifyStats>((resolve) => {
            this.asyncifyStatsResolve = resolve;
        });

        this.asyncifyStatsPromise = promise;
        this.sendClientMessage("wc-asyncify-stats", {});

        return promise;
    }

    public fsTree(): Promise<FsNode> {
        if (this.fsTreePromise !== null) {
            return this.fsTreePromise;
        }

        const promise = new Promise<FsNode>((resolve) => {
            this.fsTreeResolve = resolve;
        });
        this.fsTreePromise = promise;
        this.sendClientMessage("wc-fs-tree");

        return promise;
    }

    async fsReadFile(file: string): Promise<Uint8Array> {
        if (this.fsGetFilePromise[file] !== undefined) {
            throw new Error("fsGetFile should not be called twice for same file");
        }

        const promise = new Promise<Uint8Array>((resolve) => {
            this.fsGetFileResolve[file] = resolve;
        });
        this.fsGetFilePromise[file] = promise;
        this.fsGetFileParts[file] = [];
        this.sendClientMessage("wc-fs-get-file", {
            file,
        });

        return promise;
    }

    async fsWriteFile(file: string, contents: ReadableStream<Uint8Array> | Uint8Array): Promise<void> {
        if (ArrayBuffer.isView(contents)) {
            await this.sendDataChunk({
                type: "file",
                name: file,
                data: contents.buffer,
            });
        } else {
            const reader = contents.getReader();
            while (true) {
                const result = await reader.read();
                if (result.value !== undefined) {
                    await this.sendDataChunk({
                        type: "file",
                        name: file,
                        data: result.value.buffer,
                    });
                }
                if (result.done) {
                    break;
                }
            }
        }

        await this.sendDataChunk({
            type: "file",
            name: file,
            data: null,
        });
    }

    async fsDeleteFile(file: string): Promise<boolean> {
        if (this.fsDeleteFilePromise !== null) {
            throw new Error("fsDeleteFile should not be called while previous one is not resolved");
        }

        const promise = new Promise<boolean>((resolve) => {
            this.fsDeleteFileResolve = resolve;
        });
        this.fsDeleteFilePromise = promise;
        this.sendClientMessage("wc-fs-delete-file", { file });
        return promise;
    }

    async persistSockdrives(): Promise<PersistedSockdrives> {
        // A streamed CD (sockcd) is read-only: it never holds a write, so it
        // must not turn a title into a sockdrive one. A game that runs from a
        // bundle folder (`mount c .`) with only its disc streamed keeps its
        // saves in the filesystem, and those travel in the changes zip — with
        // the CD counted here, persist() returned an empty drive list and the
        // game's own saves were never taken.
        const writable = Object.values(this.sockdrives).filter((drive) => drive.info.kind !== "cd");
        if (writable.length === 0) {
            return null;
        }

        const drives = [];
        for (const drive of writable) {
            const persist = await drive.persist();
            if (persist !== null) {
                drives.push({
                    url: drive.info.url,
                    persist,
                });
            }
        }
        return {
            drives,
        };
    }

    private async sendDataChunk(chunk: DataChunk): Promise<void> {
        if (chunk.data === null || chunk.data.byteLength <= maxDataChunkSize) {
            return this.sendFullDataChunk(chunk);
        } else {
            let pos = 0;
            while (pos < chunk.data.byteLength) {
                await this.sendFullDataChunk({
                    type: chunk.type,
                    name: chunk.name,
                    data: chunk.data.slice(pos, Math.min(chunk.data.byteLength, pos + maxDataChunkSize)),
                });
                pos += maxDataChunkSize;
            }
        }
    }

    private async sendFullDataChunk(chunk: DataChunk): Promise<void> {
        const key = this.dataChunkKey(chunk);
        if (this.dataChunkPromise[key] !== undefined) {
            throw new Error("sendDataChunk should be accepted before sending new one");
        }
        const promise = new Promise<void>((resolve) => {
            this.dataChunkResolve[key] = resolve;
        });
        this.dataChunkPromise[key] = promise;
        this.sendClientMessage("wc-send-data-chunk", {
            chunk,
        }, chunk.data === null ? undefined : [chunk.data]);
        return promise;
    }

    private dataChunkKey(chunk: DataChunk) {
        return chunk.name;
    }

    private mergeChunks(parts: Uint8Array[]): Uint8Array {
        if (parts.length === 1) {
            return parts[0];
        }

        let length = 0;
        for (const next of parts) {
            length += next.byteLength;
        }
        const merged = new Uint8Array(length);
        length = 0;
        for (const next of parts) {
            merged.set(next, length);
            length += next.byteLength;
        }
        return merged;
    }
}
